'use client'

import { useEffect, useRef, useState } from 'react'

import { GameShell } from '@/components/losi-games/game-shell'

import { mountGameCanvas } from '@/lib/losi-games/canvas'
import { difficultyScale, type Difficulty } from '@/lib/losi-games/difficulty'
import { drawEyeCapsule, drawInkDot, drawLobeEnemy } from '@/lib/losi-games/glyphs'
import { bestLocalScore, recordScoreAndWhisper } from '@/lib/losi-games/leaderboard'
import { resolveGameTheme } from '@/lib/losi-games/theme'
import type { LosiGameRankWhisper } from '@/lib/losi-games/types'

export function EyeSnakeGame({ colorId = 'creme' }: { colorId?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [score, setScore] = useState(0)
  const [best, setBest] = useState(0)
  const [menu, setMenu] = useState(true)
  const [over, setOver] = useState(false)
  const [whisper, setWhisper] = useState<LosiGameRankWhisper | null>(null)
  const [theme, setTheme] = useState(() => resolveGameTheme(colorId))
  const themeRef = useRef(theme)
  const startFn = useRef<(d: Difficulty) => void>()
  const restartFn = useRef<() => void>()

  useEffect(() => {
    const next = resolveGameTheme(colorId)
    themeRef.current = next
    setTheme(next)
  }, [colorId])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    themeRef.current = resolveGameTheme(colorId)
    let cleanup: (() => void) | undefined

  setBest(bestLocalScore('eye-snake'))
  const { ctx, size, dispose } = mountGameCanvas(canvas)

  let cols = 20
  let rows = 18
  type Pt = { x: number; y: number }
  let snake: Pt[] = []
  let dir: Pt = { x: 1, y: 0 }
  let pending: Pt | null = null
  let food: Pt = { x: 8, y: 8 }
  let scoreAcc = 0
  let gameOver = false
  let playing = false
  let baseStep = 0.14
  let acc = 0
  let stepEvery = 0.14
  let last = performance.now()
  let alive = true
  let raf = 0

  const fitGrid = () => {
    const { w, h } = size()
    rows = 18
    cols = Math.max(18, Math.round((w / Math.max(1, h)) * rows))
  }

  const cell = () => {
    const { w, h } = size()
    return Math.min(w / cols, h / rows)
  }

  const placeFood = () => {
    for (let i = 0; i < 80; i++) {
      const p = { x: (Math.random() * cols) | 0, y: (Math.random() * rows) | 0 }
      if (!snake.some((s) => s.x === p.x && s.y === p.y)) {
        food = p
        return
      }
    }
  }

  const resetBoard = () => {
    fitGrid()
    const midY = (rows / 2) | 0
    snake = [
      { x: Math.min(4, cols - 3), y: midY },
      { x: Math.min(3, cols - 4), y: midY },
      { x: Math.min(2, cols - 5), y: midY }
    ]
    dir = { x: 1, y: 0 }
    pending = null
    scoreAcc = 0
    setScore(0)
    stepEvery = baseStep
    gameOver = false
    setOver(false)
    setWhisper(null)
    placeFood()
  }

  const endRun = () => {
    if (gameOver) return
    gameOver = true
    playing = false
    setOver(true)
    setWhisper(recordScoreAndWhisper('eye-snake', scoreAcc))
    setBest(bestLocalScore('eye-snake'))
  }

  startFn.current = (d: Difficulty) => {
    const scale = difficultyScale(d)
    // Higher difficulty → faster (smaller step interval)
    baseStep = 0.12 / scale
    setMenu(false)
    playing = true
    resetBoard()
  }

  restartFn.current = () => {
    setMenu(true)
    setOver(false)
    playing = false
    setBest(bestLocalScore('eye-snake'))
  }

  const onKey = (e: KeyboardEvent) => {
    if (!playing || gameOver) return
    const k = e.key
    let next: Pt | null = null
    if (k === 'ArrowUp' || k === 'w') next = { x: 0, y: -1 }
    if (k === 'ArrowDown' || k === 's') next = { x: 0, y: 1 }
    if (k === 'ArrowLeft' || k === 'a') next = { x: -1, y: 0 }
    if (k === 'ArrowRight' || k === 'd') next = { x: 1, y: 0 }
    if (!next) return
    e.preventDefault()
    if (next.x === -dir.x && next.y === -dir.y) return
    pending = next
  }
  window.addEventListener('keydown', onKey)

  const loop = (now: number) => {
    if (!alive) return
    const dt = Math.min(0.05, (now - last) / 1000)
    last = now
    const { w, h } = size()
    const th = themeRef.current
    const c = cell()
    const ox = (w - cols * c) / 2
    const oy = (h - rows * c) / 2

    if (playing && !gameOver) {
      acc += dt
      while (acc >= stepEvery) {
        acc -= stepEvery
        if (pending) {
          dir = pending
          pending = null
        }
        const head = snake[0]!
        const nx = head.x + dir.x
        const ny = head.y + dir.y
        if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) {
          endRun()
          break
        }
        if (snake.some((s) => s.x === nx && s.y === ny)) {
          endRun()
          break
        }
        snake.unshift({ x: nx, y: ny })
        if (nx === food.x && ny === food.y) {
          scoreAcc += 1
          setScore(scoreAcc)
          stepEvery = Math.max(0.016, baseStep / (1 + scoreAcc * 0.055))
          placeFood()
        } else snake.pop()
      }
    }

    ctx.fillStyle = th.paper
    ctx.fillRect(0, 0, w, h)
    ctx.strokeStyle = th.mute
    ctx.lineWidth = 1
    for (let i = 0; i <= cols; i++) {
      ctx.beginPath()
      ctx.moveTo(ox + i * c, oy)
      ctx.lineTo(ox + i * c, oy + rows * c)
      ctx.stroke()
    }
    for (let j = 0; j <= rows; j++) {
      ctx.beginPath()
      ctx.moveTo(ox, oy + j * c)
      ctx.lineTo(ox + cols * c, oy + j * c)
      ctx.stroke()
    }

    if (playing) {
      drawLobeEnemy(ctx, ox + (food.x + 0.5) * c, oy + (food.y + 0.5) * c, c * 0.32, 4, th.mark)
      for (let i = snake.length - 1; i >= 0; i--) {
        const s = snake[i]!
        const px = ox + (s.x + 0.5) * c
        const py = oy + (s.y + 0.5) * c
        if (i === 0) {
          const ang = Math.atan2(dir.y, dir.x) + Math.PI / 2
          drawEyeCapsule(ctx, px, py, c * 0.42, c * 0.22, ang, th.body)
        } else {
          drawInkDot(ctx, px, py, c * 0.28 * (1 - i * 0.01), th.body)
        }
      }
    }

    raf = requestAnimationFrame(loop)
  }
  raf = requestAnimationFrame(loop)
  cleanup = () => {
    alive = false
    cancelAnimationFrame(raf)
    dispose()
    window.removeEventListener('keydown', onKey)
  }

    return () => {
      cleanup?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <GameShell
      theme={theme}
      title="Snake"
      score={score}
      best={best}
      menu={menu}
      over={over}
      whisper={whisper}
      hint="Arrow keys or WASD. Don't hit the wall or yourself."
      onStart={(d) => startFn.current?.(d)}
      onRestart={() => restartFn.current?.()}
    >
      <canvas ref={canvasRef} className="block touch-none" />
    </GameShell>
  )
}
